/**
 * User Form Component
 * Form for inviting and managing users
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { inviteUserViaEdgeFunction } from "../../services/auth.service";
import { getOrganizations } from "../../services/organizations.service";
import { getLocations } from "../../services/locations.service";
import { UserRole } from "../../types/auth.types";
import type { Profile } from "../../types/auth.types";
import type { UUID } from "../../types/common.types";
import { QUERY_KEYS } from "../../utils/constants";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface UserFormProps {
  user?: Profile;
  organizationId?: UUID;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  organizationId: propOrgId,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const isEditing = !!user;
  const isSuperAdmin =
    currentUser?.role === UserRole.SUPER_ADMIN ||
    currentUser?.role === ("superadmin" as UserRole);

  // Form state
  const [formData, setFormData] = useState({
    email: user?.email || "",
    full_name: user?.full_name || "",
    first_name: "",
    last_name: "",
    role: user?.role || ("" as UserRole),
    organization_id: String(user?.organization_id || propOrgId || ""),
    phone: user?.phone_number || "",
  });

  const [selectedLocationIds, setSelectedLocationIds] = useState<UUID[]>(
    user?.location_id ? [user.location_id] : [],
  );
  const [allLocationsSelected, setAllLocationsSelected] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Organization search state
  const [orgSearch, setOrgSearch] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState("");
  const [selectedOrgFacilityId, setSelectedOrgFacilityId] = useState<
    string | null
  >(null);

  // Search organizations (only when user types 2+ chars)
  const { data: organizationsData } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATIONS, "search", orgSearch],
    queryFn: () =>
      getOrganizations({
        page: 1,
        limit: 20,
        sortBy: "name",
        sortOrder: "asc",
        search: orgSearch,
      }),
    enabled: orgSearch.length >= 2 && formData.role === UserRole.PARTNER,
  });

  // Fetch locations for selected organization
  const { data: locationsData } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: [QUERY_KEYS.LOCATIONS, formData.organization_id, "by-name"],
    queryFn: () => {
      if (!formData.organization_id || formData.organization_id === "") {
        return Promise.resolve({ data: [], total: 0, page: 1, limit: 100 });
      }
      return getLocations(formData.organization_id, {
        page: 1,
        limit: 100,
        sortBy: "name",
        sortOrder: "asc",
      });
    },
    enabled:
      !!formData.organization_id &&
      formData.organization_id !== "" &&
      typeof formData.organization_id === "string",
  });

  // Filter locations to only show those with facility_id (for Partner role)
  const locationsWithFacilityId =
    locationsData?.data?.filter((loc: any) => loc.facility_id) || [];
  const shouldShowLocationSelector =
    formData.role === UserRole.PARTNER &&
    formData.organization_id &&
    locationsWithFacilityId.length > 0;

  // Invite user mutation
  const inviteMutation = useMutation({
    mutationFn: async () => {
      const fullName =
        formData.full_name ||
        `${formData.first_name} ${formData.last_name}`.trim() ||
        "";

      // For Partner role: if "All Locations" selected, pass empty array for org-level access
      // Otherwise pass selected location IDs
      const locationIds = allLocationsSelected ? [] : selectedLocationIds;

      const result = await inviteUserViaEdgeFunction(
        formData.email,
        formData.role,
        formData.organization_id || undefined,
        locationIds,
        fullName,
      );

      return { success: true, ...result };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROFILES] });
      onSuccess?.();
    },
    onError: (error: any) => {
      console.error("Error inviting user:", error);
      setErrors({ submit: error.message || "Failed to invite user" });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation - RFC 5322 compliant
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
        formData.email,
      )
    ) {
      newErrors.email = "Invalid email format";
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    // First/last name validation (for new invitations)
    if (!isEditing) {
      if (!formData.first_name.trim()) {
        newErrors.first_name = "First name is required";
      }
      if (!formData.last_name.trim()) {
        newErrors.last_name = "Last name is required";
      }
    }

    // Organization validation - only required for Partner role
    if (formData.role === UserRole.PARTNER && !formData.organization_id) {
      newErrors.organization_id = "Organization is required for Partner role";
    }

    // Facility ID validation for Partner role
    if (formData.role === UserRole.PARTNER && formData.organization_id) {
      if (!selectedOrgFacilityId && locationsWithFacilityId.length === 0) {
        newErrors.organization_id =
          "This organization has no Facility ID. Add a Facility ID to the organization or a location before inviting a partner user.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    if (isEditing) {
      // For editing, we would need an update mutation - not implemented yet
      alert("User editing not yet implemented");
    } else {
      // Invite new user
      inviteMutation.mutate();
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Reset location selections when organization changes
    if (field === "organization_id") {
      setSelectedLocationIds([]);
      setAllLocationsSelected(false);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLocationToggle = (locationId: UUID) => {
    setSelectedLocationIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const isLoading = inviteMutation.isPending;

  // Role options — admins cannot create super_admin users
  const allRoleOptions = [
    { value: "", label: "Select Role" },
    { value: UserRole.SUPER_ADMIN, label: "Super Admin" },
    { value: UserRole.ADMIN, label: "Admin" },
    { value: UserRole.MANAGER, label: "Manager" },
    { value: UserRole.REGIONAL_MANAGER, label: "Regional Manager" },
    { value: UserRole.TERRITORY_MANAGER, label: "Territory Manager" },
    { value: UserRole.STAFF, label: "Staff" },
    { value: UserRole.READONLY, label: "Read Only" },
    { value: UserRole.PARTNER, label: "Partner" },
    { value: UserRole.FINANCE, label: "Finance" },
    { value: UserRole.SUPPORT_ADMIN, label: "Support Admin" },
    { value: UserRole.SUPPORT_AGENT, label: "Support Agent" },
    { value: UserRole.IT, label: "IT" },
    { value: UserRole.MSR, label: "MSR (Membership Sales Rep)" },
  ];

  // Non-superadmin users (admin, IT) cannot create superadmin users
  const roleOptions = isSuperAdmin
    ? allRoleOptions
    : allRoleOptions.filter((opt) => opt.value !== UserRole.SUPER_ADMIN);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="First Name"
          value={formData.first_name}
          onChange={(e) => handleChange("first_name", e.target.value)}
          error={errors.first_name}
          required={!isEditing}
          disabled={isEditing}
        />

        <Input
          label="Last Name"
          value={formData.last_name}
          onChange={(e) => handleChange("last_name", e.target.value)}
          error={errors.last_name}
          required={!isEditing}
          disabled={isEditing}
        />

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={errors.email}
          required
          disabled={isEditing}
        />

        <Input
          label="Phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          error={errors.phone}
        />
      </div>

      <Select
        label="Role"
        value={formData.role}
        onChange={(e) => handleChange("role", e.target.value as UserRole)}
        error={errors.role}
        required
      >
        {roleOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      {formData.role === UserRole.MSR && (
        <p className="text-xs text-gray-500 -mt-4" data-testid="msr-role-hint">
          An MSR sees only the events their sales position is scheduled on, and
          an account invited here is not linked to a position. Invite reps from
          their position's page in Sales Team instead, so the account is linked.
        </p>
      )}

      {/* Organization search dropdown - only shown for Partner role */}
      {formData.role === UserRole.PARTNER && (
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organization <span className="text-red-500">*</span>
          </label>
          {formData.organization_id && selectedOrgName ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm">
                {selectedOrgName}
              </div>
              <button
                type="button"
                onClick={() => {
                  handleChange("organization_id", "");
                  setSelectedOrgName("");
                  setSelectedOrgFacilityId(null);
                  setOrgSearch("");
                }}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={orgSearch}
                onChange={(e) => {
                  setOrgSearch(e.target.value);
                  setShowOrgDropdown(e.target.value.length >= 2);
                }}
                onFocus={() => {
                  if (orgSearch.length >= 2) {
                    setShowOrgDropdown(true);
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on dropdown item
                  setTimeout(() => setShowOrgDropdown(false), 200);
                }}
                onKeyDown={preventEnterSubmit}
                placeholder="Type to search organizations..."
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.organization_id ? "border-red-500" : "border-gray-300"
                }`}
              />
              {orgSearch.length > 0 && orgSearch.length < 2 && (
                <p className="mt-1 text-xs text-gray-400">
                  Type at least 2 characters to search
                </p>
              )}
              {showOrgDropdown && organizationsData?.data && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {organizationsData.data.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No organizations found
                    </div>
                  ) : (
                    organizationsData.data.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          handleChange("organization_id", org.id);
                          setSelectedOrgName(org.name);
                          setSelectedOrgFacilityId(org.facility_id || null);
                          setOrgSearch("");
                          setShowOrgDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-700"
                      >
                        {org.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
          {errors.organization_id && (
            <p className="mt-1 text-sm text-red-600">
              {errors.organization_id}
            </p>
          )}
        </div>
      )}

      {/* Partner Location Selection */}
      {shouldShowLocationSelector && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Location Access
          </label>

          {/* "All Locations" Option */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={allLocationsSelected}
                onChange={(e) => {
                  setAllLocationsSelected(e.target.checked);
                  if (e.target.checked) {
                    setSelectedLocationIds([]);
                  }
                }}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm font-semibold text-gray-900">
                All Locations
              </span>
            </label>

            {/* Individual Locations */}
            {!allLocationsSelected && (
              <div className="pl-6 space-y-2 border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-600 mb-2">
                  Or select specific locations:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {locationsWithFacilityId.map((location: any) => (
                    <label
                      key={location.id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLocationIds.includes(location.id)}
                        onChange={() => handleLocationToggle(location.id)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {location.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500">
            {allLocationsSelected
              ? "This partner will have access to all locations within the organization."
              : selectedLocationIds.length > 0
                ? `This partner will have access to ${selectedLocationIds.length} selected location(s).`
                : 'Please select "All Locations" or choose specific locations.'}
          </p>
        </div>
      )}

      {!isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            An invitation email will be sent to {formData.email || "the user"}{" "}
            with instructions to set up their account.
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Sending Invitation..."
            : isEditing
              ? "Update User"
              : "Send Invitation"}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
